import EventDispatcher from '../event/event-dispatcher';

/**
 * Type for pagination settings.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export interface PaginationSettings {
  'start': number,
  'rows': number,
  'numFound': number,
  'maxLinks': number
}


/**
 * Pagination for the any countable results.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export default class Pagination {
  /**
   * Event for checking if the pagination is ready.
   *
   * @type {EventDispatcher<void>}
   * @private
   */
  private readonly _readyEvent = new EventDispatcher<void>();

  /**
   * Event for checking if the page has changed.
   *
   * @type {EventDispatcher<PaginationSettings>}
   * @private
   */
  private readonly _browseEvent = new EventDispatcher<PaginationSettings>();

  /**
   * Current pagination settings.
   *
   * @type {PaginationSettings}
   * @private
   */
  private _settings: PaginationSettings;

  /**
   * Initially contains the handlebars template. Once rendered, this container will hold the final pagination.
   *
   * @type HTMLElement
   * @private
   */
  private readonly _container: HTMLElement;


  /**
   * Constructor.
   *
   * @param {PaginationSettings} settings Initial pagination settings
   * @param {HTMLElement} container Container that will be used as a target for the pagination
   */
  constructor(settings: PaginationSettings, container: HTMLElement) {
    this._settings = settings;
    this._container = container;

    this.redraw();
    this.readyEvent.dispatch();
  }


  /**
   * Gets the event for checking if the pagination is ready.
   *
   * @returns {EventDispatcher<void>}
   */
  get readyEvent(): EventDispatcher<void> {
    return this._readyEvent;
  }


  /**
   * Gets the event for checking if the page has changed.
   *
   * @returns {EventDispatcher<PaginationSettings>}
   */
  get browseEvent(): EventDispatcher<PaginationSettings> {
    return this._browseEvent;
  }


  /**
   * Gets the pagination settings.
   *
   * @returns {PaginationSettings}
   */
  get settings(): PaginationSettings {
    return this._settings;
  }


  /**
   * Sets the pagination settings.
   *
   * @returns {void}
   */
  set settings(settings: PaginationSettings) {
    this._settings = settings;
  }


  /**
   * Gets the target container.
   *
   * @returns {HTMLElement}
   */
  get container(): HTMLElement {
    return this._container;
  }


  /**
   * Redraws the pagination with the current settings.
   *
   * @returns {void}
   */
  redraw(): void {
    // If no results are present, hide pagination
    if (this.settings.numFound === 0) {
      this.container.classList.add('d-none');
      return;
    }
    this.container.classList.remove('d-none');

    // Calculate new pagination settings
    const paginationParameters = this.calculatePaginationParameters();

    // Render handlebars template
    const template = require('../templates/pagination.handlebars');
    this.container.innerHTML = template(paginationParameters);

    // Initialize new pagination buttons
    this.initializeBrowser();
  }


  /**
   * Browser to a new page.
   *
   * @param {number} start New start index
   * @param {number} [rows] New number of rows to list (defaults to the previously used setting/default setting)
   * @param {boolean} [dispatchEvents] Whether to dispatch events or not (defaults to true)
   * @returns {void}
   */
  browse(start: number, rows: number = 0, dispatchEvents: boolean = true): void {
    this.settings.start = start;

    if (rows > 0) {
      this.settings.rows = rows;
    }

    this.redraw();

    if (dispatchEvents) {
      this.browseEvent.dispatch(this.settings);
    }
  }


  /**
   * Updates the number of items being paginated.
   *
   * @param {number} numFound Number of items
   * @returns {void}
   */
  updateNumFound(numFound: number): void {
    this.settings.numFound = numFound;
    this.redraw();
  }


  /**
   * Resets the pagination to the first page.
   *
   * @param {number} [numFound] Optionally force a new number of items to be paginated (defaults to nothing)
   * @returns {void}
   */
  reset(numFound: number = null): void {
    this.settings.start = 0;

    if (numFound !== null) {
      this.settings.numFound = numFound;
    }

    this.redraw();
  }


  /**
   * Initializes the pagination buttons.
   *
   * @returns {void}
   * @private
   */
  private initializeBrowser(): void {
    const buttons = this.container.querySelectorAll('[data-page]');
    buttons.forEach((button: HTMLElement) => {
      const start = parseInt(button.dataset.start);
      const rows = parseInt(button.dataset.rows);

      button.addEventListener('click', e => {
        e.preventDefault();
        this.browse(start, rows);
      });
    });
  }


  /**
   * Calculates new pagination parameters. Current pagination settings are used as a base for calculations.
   *
   * @returns { [key: string]: any } Pagination parameters
   * @private
   */
  private calculatePaginationParameters(): { [key: string]: any } {
    const settings = this.settings;

    // Number of links needs to be uneven and greater than three, for the pagination to work in all variants
    if (settings.maxLinks < 3) {
      settings.maxLinks = 3;
    }
    if (settings.maxLinks % 2 === 0) {
      settings.maxLinks++;
    }

    // Calculate current page
    let page: number = 1;
    if (settings.start >= settings.rows) {
      page = (settings.start / settings.rows) + 1;
    }

    // Calculate total amount of pages
    const totalPages: number = Math.ceil(settings.numFound / settings.rows);

    // Calculate start and end indices
    let first: { [key: string]: number } = null;
    let last: { [key: string]: number } = null;
    const half: number = settings.maxLinks - Math.ceil(settings.maxLinks / 2);

    let start: number = page - half;
    if (start <= 0) {
      start = 1;
    }

    let end: number = start + settings.maxLinks;
    if (end > totalPages) {
      start -= end - totalPages - 1;
      end = totalPages + 1; // +1 to account for current page
    }

    if (settings.maxLinks >= totalPages) {
      start = 1;
      end = totalPages + 1;
    }

    // Create initial page list
    const pages: { [key: string]: number }[] = [];

    for (let i = start; i < end; i++) {
      pages.push({
        'page': i,
        'start': settings.rows * (i - 1),
        'rows': settings.rows
      });
    }

    if (totalPages > settings.maxLinks) {
      // The very first page needs to be shown, as the current page has been moved ahead far enough
      if (page !== 1 && page > half + 1) {
        first = {
          'page': 1,
          'start': 0,
          'rows': settings.rows
        };
      }

      // The very last page needs to be shown, as the current page has been moved behind far enough
      if (page !== totalPages && page < totalPages - half) {
        last = {
          'page': totalPages,
          'start': settings.rows * (totalPages - 1),
          'rows': settings.rows
        };
      }
    }

    // Calculate previous and next parameters
    let previous: { [key: string]: number } = null;
    let next: { [key: string]: number } = null;

    if (page > 1) {
      previous = {
        'page': page - 1,
        'start': settings.rows * (page - 2),
        'rows': settings.rows
      };
    }

    if (page !== totalPages) {
      next = {
        'page': page + 1,
        'start': settings.rows * page,
        'rows': settings.rows
      };
    }

    return {
      'itemsPerPage': settings.rows,
      'numFound': settings.numFound,
      'totalPages': totalPages,
      'selectedPage': page,
      'pages': pages,
      'previous': previous,
      'next': next,
      'first': first,
      'last': last
    };
  }
}
